import { useEffect, useState, ChangeEvent } from 'react'
import { useSelector } from 'react-redux'
import { getLocaleSelector, getGalleryIdSelector, getMpvid, getMarket, getH1Title, getQuantitySelector } from 'client/store/config/reducer'
import { ANALYTICS_CATEGORY, ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENT_DETAILS, ANALYTICS_LABEL, AnalyticsNames, PREVIEW_TYPE } from 'shared/constants'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFavorite } from 'client/mutations/createFavorite'
import { deleteFavorite } from 'client/mutations/deleteFavorite'
import { QUERY_KEY } from 'client/queries/queryFavorites'
import { PAGE_ACTION_TYPES, WES_TENANT } from 'client/constants'
import { useAnalytics } from 'client/hooks/gallery/useAnalytics'
import { instrumenter } from 'client/utils/instrumentation'
import getLogger from 'shared/logger'
import { galleryImpressionIdSelector } from '~/client/store/analytics/reducer'
import { UseFavoriteParameters, UseFavoriteResult } from '~/client/components/Gallery/DesignTile/interface'
import { useTranslations } from '~/client/hooks/useTranslations'
import { getFavoritesAction } from '~/client/components/common/FavoriteButton/utils'
import { useIdentity } from '@vp/ubik-context'

const getLocationFromPreviewType = (previewType: PREVIEW_TYPE): string => {
  switch (previewType) {
    case PREVIEW_TYPE.QUICKVIEW:
      return AnalyticsNames.Quickview
    default:
      return AnalyticsNames.DesignTile
  }
}

export const useFavorite = ({ entityId, previewType, colorSwatches, favoriteId, tileEntity, defaultColorSwatch }: UseFavoriteParameters): UseFavoriteResult => {
  const locale = useSelector(getLocaleSelector)
  const mpvId = useSelector(getMpvid)
  const galleryId = useSelector(getGalleryIdSelector)
  const market = useSelector(getMarket)
  const galleryH1Title = useSelector(getH1Title)
  const quantity = useSelector(getQuantitySelector)
  const localize = useTranslations()
  const { auth, identity } = useIdentity()
  const queryClient = useQueryClient()
  const [isFavorite, setIsFavorite] = useState<boolean>(!!favoriteId)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isDisabled, setDisabled] = useState(false)
  const location = getLocationFromPreviewType(previewType)
  const impressionId = useSelector((state: State.GlobalState) => galleryImpressionIdSelector(state, entityId))

  useEffect(() => {
    setIsFavorite(!!favoriteId)
  }, [favoriteId])

  const createFavoriteMutation = useMutation({
    mutationFn: createFavorite,
    onMutate: () => {
      setIsFavorite(true)
    },
    onError: () => {
      setIsFavorite(false)
    },
  })
  const deleteFavoriteMutation = useMutation({
    mutationFn: deleteFavorite,
    onMutate: () => {
      setIsFavorite(false)
    },
    onError: () => {
      setIsFavorite(true)
    },
  })

  const analytics = useAnalytics()
  const ariaLabel = isFavorite ? localize('FavoritesRemoveMessage') : localize('FavoritesAddMessage')

  const handleClick = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    event.preventDefault()
    event.stopPropagation()

    if (!favoriteId) {
      const newFavorite: VP.DTT.Models.WorkEntityService.WorkEntity<Gallery.ContentQuery.PreviewInfo> = {
        workName: `${locale}_${galleryId}`,
        merchandising: {
          merchandisingSelections: {
            fullProductOptions: tileEntity.fullProductOptions,
            productOptions: tileEntity.productOptions,
          },
          metadata: {
            comparativeName: tileEntity.comparativeName || ``,
            locale,
            market,
            mpvId,
            galleryId,
            galleryH1Title,
          },
          quantity: quantity || 0,
        },
        design: {
          editUrl: defaultColorSwatch.studioUrl,
          metadata: {
            templateToken: tileEntity.previewInfo.templateToken,
            designId: tileEntity.designId,
            previewUrlSize1x: defaultColorSwatch.renderablePreviews.previewsUrls.size1x,
            previewUrlSize2x: defaultColorSwatch.renderablePreviews.previewsUrls.size2x,
            previewInfo: tileEntity.previewInfo,
          },
        },
        product: {
          key: tileEntity.productKey,
          version: tileEntity.productVersion,
        },
        isFavorite: true,
      }
      // optionally. Update wes query optimisitcally

      try {
        await createFavoriteMutation.mutateAsync({
          tenant: WES_TENANT,
          newFavorite,
          ownerId: auth?.getCanonicalId(),
          accessToken: identity?.accessToken,
        })

        queryClient.cancelQueries({ queryKey: [QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })

        analytics.trackEvent({
          action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
          category: ANALYTICS_CATEGORY.ALL,
          eventDetail: ANALYTICS_EVENT_DETAILS.FAVORITE_ADDED,
          eventLabel: ANALYTICS_LABEL.FAVORITE_ADDED,
          ...analytics.buildDesignEngagement({
            engagementAction: ANALYTICS_EVENT_DETAILS.FAVORITE_ADDED,
            selectedDesign: tileEntity.designId,
            color: defaultColorSwatch.color,
            tileEntity,
            colorSwatchObjects: colorSwatches,
            location,
            impressionId,
            position: tileEntity.position,
          }),
        })
      } catch (e) {
        getLogger().warn(`Unable to add design to Favorites: ${newFavorite.design.metadata.designId}`, e as Error, { updateFavoriteHere: newFavorite })
        throw e
      }
    } else {
      try {
        await deleteFavoriteMutation.mutateAsync({
          favoriteId,
          accessToken: identity?.accessToken,
        })

        // cancel and then invalidate (invalidate using the filter predicate) and refetch  wes query
        // see ingest code for examples
        queryClient.cancelQueries({ queryKey: [QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })

        analytics.trackEvent({
          action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
          category: ANALYTICS_CATEGORY.ALL,
          eventDetail: ANALYTICS_EVENT_DETAILS.FAVORITE_REMOVED,
          eventLabel: ANALYTICS_LABEL.FAVORITE_REMOVED,
          ...analytics.buildDesignEngagement({
            engagementAction: ANALYTICS_EVENT_DETAILS.FAVORITE_REMOVED,
            selectedDesign: tileEntity.designId,
            color: defaultColorSwatch.color,
            tileEntity,
            colorSwatchObjects: colorSwatches,
            impressionId,
            location,
            position: tileEntity.position,
          }),
        })
      } catch (e) {
        getLogger().warn(`Unable to remove design from Favorites: ${tileEntity.designId}`, e as Error, { updateFavoriteHere: favoriteId })
        throw e
      }
    }
  }

  const onChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    try {
      if (isDisabled) {
        return
      }

      setDisabled(true)

      instrumenter.recordPageAction(PAGE_ACTION_TYPES.FAVORITE_BUTTON_CLICKED, {
        action: getFavoritesAction(isFavorite),
      })
      await handleClick(e)
    } catch (error) {
      setErrorMessage(localize(isFavorite ? 'FavoritesRemoveErrorMessage' : 'FavoritesAddErrorMessage'))
      instrumenter.recordPageAction(PAGE_ACTION_TYPES.FAVORITES_UPDATE_FAILED, {
        info: error,
        action: getFavoritesAction(isFavorite),
      })
    } finally {
      setDisabled(false)
    }
  }

  const handleErrorDismiss = (): void => {
    setErrorMessage(null)
  }

  return {
    isFavorite,
    ariaLabel,
    onChange,
    onRequestDismiss: handleErrorDismiss,
    errorMessage,
    dismissVisuallyHiddenLabel: localize('DismissAlert'),
  }
}
